"use client";

import React, { createContext, useContext, useMemo } from "react";
import { type CorrectAnswerType, type HiddenCorrectAnswersType } from "@/lib/problemAnswerUtils";
import type { RelatedTheoryLink } from "@/lib/related-theory";

// 문제 데이터 타입 (ProblemAnswerFetcher에서 사용하는 것과 동일)
interface ProblemData {
  problem: string;
  correctAnswer: CorrectAnswerType;
  examSessions?: string[];
  choices?: string[];
  solution?: string;
  shuffle?: boolean;
  ignoreOrder?: boolean;
  choiceCopy?: boolean;
  alwaysShowChoices?: boolean;
  strictWhitespace?: boolean;
  caseSensitive?: boolean;
  compareAsSet?: boolean;
  allowQuotedSqlAlias?: boolean;
  notRecommended?: boolean;
  notRecommendedReason?: string;
  problemType?: "기초" | "비추천" | "기출변형" | "기출";
  problemTypes?: ("기초" | "비추천" | "기출변형" | "기출")[];
  variationNote?: string;
  gridCols?: 2 | 3 | 4;
  hiddenCorrectAnswers?: HiddenCorrectAnswersType;
  tags?: string[];
  answerSeparator?: string;
  premium?: boolean;
  relatedTheory?: RelatedTheoryLink[];
}

interface ProblemDataContextType {
  problems: Record<string, ProblemData | null>;
  isLoading: boolean;
}

const ProblemDataContext = createContext<ProblemDataContextType>({
  problems: {},
  isLoading: false,  // Provider가 없을 때는 fallback fetch가 동작하도록 false
});

interface ProblemDataProviderProps {
  children: React.ReactNode;
  problems: Record<string, ProblemData | null>;
  isLoading: boolean;
}

export function ProblemDataProvider({
  children,
  problems,
  isLoading,
}: ProblemDataProviderProps) {
  const value = useMemo(() => ({ problems, isLoading }), [problems, isLoading]);
  return (
    <ProblemDataContext.Provider value={value}>
      {children}
    </ProblemDataContext.Provider>
  );
}

export function useProblemData() {
  return useContext(ProblemDataContext);
}

export type { ProblemData };
